import axios, { AxiosError, type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Не показывать тост с ошибкой — вызывающий код покажет её сам или она не важна. */
    silent?: boolean;
  }
}

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';
const PREFIX = '/api/courier';

export const api: AxiosInstance = axios.create({ baseURL, timeout: 20_000 });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['Authorization'] = `Bearer ${t}`;
  }
  return cfg;
});

let refreshInflight: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${baseURL}${PREFIX}/auth/refresh`,
      { refresh_token: refresh },
      { timeout: 10_000 },
    );
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch (err) {
    // Нет сети — сессию не трогаем: курьер в подвале магазина не должен вылетать.
    const status = (err as AxiosError).response?.status;
    if (status === 401 || status === 403) useAuthStore.getState().logout();
    return null;
  }
}

/** Один refresh на пачку 401. */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInflight) {
    refreshInflight = refreshOnce().finally(() => {
      refreshInflight = null;
    });
  }
  return refreshInflight;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string }>) => {
    const status = err.response?.status;
    const original = err.config as (typeof err.config & { _retry?: boolean }) | undefined;
    const msg = err.response?.data?.error ?? err.message ?? 'Ошибка запроса';

    if (status === 401 && original && !original._retry && !(original.url ?? '').includes('/auth/')) {
      original._retry = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        return api(original);
      }
      if (!useAuthStore.getState().refreshToken) toast.error('Сессия истекла, войдите ещё раз');
      return Promise.reject(err);
    }
    // Выходим только при блокировке аккаунта: 403 бывает и на обычные запреты
    // (например, удалить чужое сообщение в чате).
    if (status === 403 && /заблокирован/i.test(msg) && !(original?.url ?? '').includes('/auth/')) {
      useAuthStore.getState().logout();
      toast.error(msg);
      return Promise.reject(err);
    }
    if (!original?.silent) {
      // Фоновые чтения без сети не шумят — об этом говорит полоса «Нет сети».
      if (!err.response) {
        if ((original?.method ?? 'get').toLowerCase() !== 'get') toast.error('Нет связи с сервером');
      } else if (status && status >= 400 && status < 500) toast.error(msg);
      else if (status && status >= 500) toast.error(`Ошибка сервера: ${msg}`);
    }
    return Promise.reject(err);
  },
);

/** Запрос не дошёл до сервера (нет сети, таймаут) — можно повторить позже. */
export function isNetworkError(err: unknown): boolean {
  const e = err as AxiosError;
  return !!e?.isAxiosError && !e.response;
}

export async function get<T>(url: string, params?: Record<string, unknown>, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.get<T>(`${PREFIX}${url}`, { params, silent: opts?.silent });
  return data;
}

export async function post<T>(url: string, body?: unknown, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${url}`, body, { silent: opts?.silent });
  return data;
}

export async function patch<T>(url: string, body?: unknown, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.patch<T>(`${PREFIX}${url}`, body, { silent: opts?.silent });
  return data;
}

export async function del<T>(url: string, opts?: { silent?: boolean }): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${url}`, { silent: opts?.silent });
  return data;
}

// ─── Те же имена, что в кабинете Оптовика, — общий код чата и пушей ───────

export const apiGet = <T>(path: string, opts?: { silent?: boolean }) => get<T>(path, undefined, opts);
export const apiPost = <T>(path: string, body?: unknown, opts?: { silent?: boolean }) => post<T>(path, body, opts);
export const apiPatch = <T>(path: string, body: unknown, opts?: { silent?: boolean }) => patch<T>(path, body, opts);

export async function apiDelete<T>(path: string, opts?: { silent?: boolean; body?: unknown }): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${path}`, { silent: opts?.silent, data: opts?.body });
  return data;
}

export async function apiUpload<T>(
  path: string,
  form: FormData,
  opts?: { silent?: boolean; onProgress?: (ratio: number) => void },
): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${path}`, form, {
    silent: opts?.silent,
    timeout: 120_000,
    onUploadProgress: (e) => {
      if (e.total) opts?.onProgress?.(Math.min(1, e.loaded / e.total));
    },
  });
  return data;
}

export function apiErrorMessage(err: unknown, fallback = 'Не удалось выполнить действие'): string {
  const e = err as AxiosError<{ error?: string }>;
  if (e?.response?.status === 413) return 'Файл слишком большой';
  if (e?.isAxiosError && !e.response) return 'Нет связи с сервером';
  return e?.response?.data?.error ?? (e?.message || fallback);
}
